import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { submitLead } from '../services/hubspot';
import { CheckCircle, XCircle, Phone } from 'lucide-react';
import PrivacyCheckbox from './PrivacyCheckbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ValuationForm({ variant = 'full' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    zip_code: '',
    city: '',
    address: '',
    building_year: '',
    living_area: '',
    plot_area: '',
    rooms: '',
    special_features: '',
    preferred_contact: 'phone'
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) return;
    
    setLoading(true);
    setStatus(null);

    const hubspotResult = await submitLead('wertermittlung', {
  name: formData.name,
  email: formData.email,
  phone: formData.phone,
  propertyType: formData.property_type,
  zipCode: formData.zip_code,
  city: formData.city,
  address: formData.address,
  livingArea: formData.living_area,
  buildingYear: formData.building_year,
  rooms: formData.rooms,
  landArea: formData.plot_area,
  preferredContact: formData.preferred_contact,
  message: formData.special_features
});

    try {
      await axios.post(`${API}/api/valuation`, {
        ...formData,
        building_year: formData.building_year ? parseInt(formData.building_year) : null,
        living_area: formData.living_area ? parseFloat(formData.living_area) : null,
        plot_area: formData.plot_area ? parseFloat(formData.plot_area) : null,
        rooms: formData.rooms ? parseFloat(formData.rooms) : null
      });
    } catch (error) {
      console.error('Backend submission error:', error);
    }

    setLoading(false);
    
    if (hubspotResult.success) {
      setStatus('success');
      setFormData({
        name: '', email: '', phone: '', property_type: '', zip_code: '', city: '',
        address: '', building_year: '', living_area: '', plot_area: '', rooms: '',
        special_features: '', preferred_contact: 'phone'
      });
      setPrivacyAccepted(false);
    } else {
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const StatusMessage = () => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`p-4 mb-6 flex items-start gap-3 ${
            status === 'success' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {status === 'success' ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">Vielen Dank!</p>
                <p className="text-green-400/80 text-sm">Serdar Dogan meldet sich in Kürze bei Ihnen.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Es ist ein Fehler aufgetreten.</p>
                <p className="text-red-400/80 text-sm">
                  Bitte rufen Sie uns direkt an: 
                  <a href="tel:+491756900374" className="ml-1 inline-flex items-center gap-1 text-[#d1af6a] hover:underline">
                    <Phone className="w-3 h-3" /> +49 175 6900374
                  </a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="valuation-form-compact">
        <StatusMessage />
        
        {status !== 'success' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                placeholder="Ihr Name *"
                required
                value={formData.name}
                onChange={handleChange}
                className="input-luxury w-full"
              />
              <input
                type="email"
                name="email"
                placeholder="E-Mail *"
                required
                value={formData.email}
                onChange={handleChange}
                className="input-luxury w-full"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Telefonnummer *"
                required
                value={formData.phone}
                onChange={handleChange}
                className="input-luxury w-full"
              />
              <Select
                value={formData.property_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}
              >
                <SelectTrigger className="input-luxury w-full h-auto">
                  <SelectValue placeholder="Art der Immobilie *" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                  <SelectItem value="Wohnung">Wohnung</SelectItem>
                  <SelectItem value="Haus">Haus</SelectItem>
                  <SelectItem value="Grundstück">Grundstück</SelectItem>
                  <SelectItem value="Gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input
              type="text"
              name="zip_code"
              placeholder="PLZ / Ort *"
              required
              value={formData.zip_code}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <PrivacyCheckbox 
              checked={privacyAccepted} 
              onCheckedChange={setPrivacyAccepted} 
            />
            <button
              type="submit"
              disabled={loading || !privacyAccepted}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="valuation-submit"
            >
              {loading ? 'Wird gesendet…' : 'Kostenlose Bewertung anfordern'}
            </button>
          </>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="valuation-form-full">
      <StatusMessage />
      
      {status !== 'success' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              name="name"
              placeholder="Ihr Name *"
              required
              value={formData.name}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <input
              type="email"
              name="email"
              placeholder="E-Mail *"
              required
              value={formData.email}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Telefonnummer *"
              required
              value={formData.phone}
              onChange={handleChange}
              className="input-luxury w-full"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="address"
              placeholder="Adresse der Immobilie"
              value={formData.address}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                name="zip_code"
                placeholder="PLZ *"
                required
                value={formData.zip_code}
                onChange={handleChange}
                className="input-luxury w-full"
              />
              <input
                type="text"
                name="city"
                placeholder="Ort"
                value={formData.city}
                onChange={handleChange}
                className="input-luxury w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              value={formData.property_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}
            >
              <SelectTrigger className="input-luxury w-full h-auto">
                <SelectValue placeholder="Immobilientyp *" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                <SelectItem value="Wohnung">Wohnung</SelectItem>
                <SelectItem value="Haus">Haus</SelectItem>
                <SelectItem value="Grundstück">Grundstück</SelectItem>
                <SelectItem value="Gewerbe">Gewerbe</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="number"
              name="building_year"
              placeholder="Baujahr"
              value={formData.building_year}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <input
              type="number"
              name="living_area"
              placeholder="Wohnfläche (m²)"
              value={formData.living_area}
              onChange={handleChange}
              className="input-luxury w-full"
            />
            <input
              type="number"
              name="rooms"
              placeholder="Zimmer"
              step="0.5"
              value={formData.rooms}
              onChange={handleChange}
              className="input-luxury w-full"
            />
          </div>

          {(formData.property_type === 'Haus' || formData.property_type === 'Grundstück') && (
            <input
              type="number"
              name="plot_area"
              placeholder="Grundstücksfläche (m²)"
              value={formData.plot_area}
              onChange={handleChange}
              className="input-luxury w-full md:w-1/2"
            />
          )}

          <textarea
            name="special_features"
            placeholder="Besonderheiten (optional)"
            rows={3}
            value={formData.special_features}
            onChange={handleChange}
            className="input-luxury w-full resize-none"
          />

          <div>
            <p className="text-[#c8c6c4] text-sm mb-3">Wie darf ich Sie kontaktieren?</p>
            <div className="flex flex-wrap gap-4">
              {['phone', 'email', 'whatsapp'].map((method) => (
                <label key={method} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="preferred_contact"
                    value={method}
                    checked={formData.preferred_contact === method}
                    onChange={handleChange}
                    className="w-4 h-4 accent-[#d1af6a]"
                  />
                  <span className="text-white text-sm">
                    {method === 'phone' && 'Telefon'}
                    {method === 'email' && 'E-Mail'}
                    {method === 'whatsapp' && 'WhatsApp'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <PrivacyCheckbox 
            checked={privacyAccepted} 
            onCheckedChange={setPrivacyAccepted} 
          />

          <button
            type="submit"
            disabled={loading || !privacyAccepted}
            className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="valuation-submit"
          >
            {loading ? 'Wird gesendet…' : 'Kostenlose Bewertung anfordern'}
          </button>
        </>
      )}
    </form>
  );
}
